import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// Maps an applyBlueprint status string to a MUI Chip color. Statuses come from the backend:
// Created / Updated, AlreadyExists / Unchanged, WouldCreate / WouldUpdate (dry run), Skipped,
// Conflict, Failed.
const statusColor = (status) => {
  switch (status) {
    case "Created":
    case "Updated":
      return "success";
    case "AlreadyExists":
    case "Unchanged":
      return "default";
    case "WouldCreate":
    case "WouldUpdate":
      return "info";
    case "Skipped":
    case "Conflict":
      return "warning";
    case "Failed":
      return "error";
    default:
      return "default";
  }
};

const StatusChip = ({ status }) => (
  <Chip size="small" label={status || "—"} color={statusColor(status)} />
);

// Renders the structured outcome of an applyBlueprint call (dry run or real). Used by every deploy
// tab in place of the old per-tab progress/completed/failed tracking.
const ApplyBlueprintResultReport = ({ result, loading, errors }) => {
  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} my={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Applying blueprint…</Typography>
      </Box>
    );
  }

  if (errors?.length) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {errors.map((e, i) => (
          <div key={i}>{e.message}</div>
        ))}
      </Alert>
    );
  }

  if (!result) {
    return null;
  }

  const { dryRun, warnings = [], standards = [], instances = [], columns = [] } =
    result;

  return (
    <Box my={2}>
      {dryRun && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Dry run — no changes were written. Review the plan below, then run again with dry run off to
          apply.
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </Alert>
      )}

      {standards.length > 0 && (
        <ResultTable
          title="Policy Templates (standards)"
          columns={["Name", "Status", "Detail"]}
          rows={standards.map((s) => [s.name, <StatusChip status={s.status} />, s.message])}
        />
      )}

      {instances.length > 0 && (
        <ResultTable
          title="Rule Instances"
          columns={["Target Source", "Instance", "Status", "Detail"]}
          rows={instances.map((i) => [
            i.targetSourceName,
            i.ruleInstanceName,
            <StatusChip status={i.status} />,
            <InstanceDetail instance={i} />,
          ])}
        />
      )}

      {columns.length > 0 && (
        <ResultTable
          title="Column Configurations"
          columns={["Target Source", "Column", "Status", "Detail"]}
          rows={columns.map((c) => [
            c.targetSourceName,
            c.columnName,
            <StatusChip status={c.status} />,
            c.message,
          ])}
        />
      )}

      {standards.length === 0 &&
        instances.length === 0 &&
        columns.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing to deploy for this blueprint and scope.
          </Typography>
        )}
    </Box>
  );
};

// Instance rows can carry unmatched columns + per-instance error messages alongside the detail text.
const InstanceDetail = ({ instance }) => (
  <>
    {instance.message && <div>{instance.message}</div>}
    {instance.errors?.map((e, i) => (
      <div key={`err-${i}`} style={{ color: "#c62828" }}>
        {e}
      </div>
    ))}
    {instance.unmatchedColumns?.length > 0 && (
      <div style={{ color: "#ed6c02" }}>
        Unmatched columns: {instance.unmatchedColumns.join(", ")}
      </div>
    )}
  </>
);

const ResultTable = ({ title, columns, rows }) => (
  <Box mb={3}>
    <Typography variant="subtitle1" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, r) => (
            <TableRow key={r}>
              {cells.map((cell, c) => (
                <TableCell key={c}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default ApplyBlueprintResultReport;
